'use client';

import { useCallback, useMemo } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useDomainTreeCollapsedStore } from '../stores/domainTreeCollapsedStore';

function useDomainTreeRouteKey(): string {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const intent = searchParams.get('intent');
  const subtab = searchParams.get('subtab');

  return useMemo(() => {
    if (intent && subtab) {
      return `${intent}:${subtab}`;
    }

    return pathname ?? 'default';
  }, [intent, subtab, pathname]);
}

export function useDomainTreeCollapsed(disableCollapsible = false) {
  const routeKey = useDomainTreeRouteKey();
  const storeHasHydrated = useDomainTreeCollapsedStore(
    (state) => state._hasHydrated,
  );
  const isCollapsedFromStore = useDomainTreeCollapsedStore((state) =>
    state.getIsCollapsed(routeKey),
  );
  const setStoreCollapsed = useDomainTreeCollapsedStore(
    (state) => state.setIsCollapsed,
  );

  const isCollapsed = disableCollapsible
    ? false
    : storeHasHydrated
      ? isCollapsedFromStore
      : false;

  const setIsDomainTreeCollapsed = useCallback(
    (value: boolean) => {
      if (disableCollapsible) return;
      setStoreCollapsed(routeKey, value);
    },
    [disableCollapsible, routeKey, setStoreCollapsed],
  );

  const isCollapsible = !disableCollapsible;

  return { isCollapsed, setIsDomainTreeCollapsed, isCollapsible };
}
